import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, makeStyles, CircularProgress } from '@material-ui/core';
import SendIcon from '@material-ui/icons/Send';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import axios from 'axios';
import moment from 'moment';
import jwtDecode from "jwt-decode";

import { appUrl } from '../../../../../../utils/config';

const getApiBase = () =>
  `${appUrl}/${localStorage.getItem('appName')}/${localStorage.getItem('network')}/${localStorage.getItem('collabType')}/${localStorage.getItem('roleName')}`;

const useStyles = makeStyles(() => ({
  wrapper: {
    padding: '8px 0',
  },
  card: {
    border: '2px solid #1976d2',
    borderRadius: '8px',
    overflow: 'hidden',
    width: '450px',
    maxWidth: '100%',
  },
  cardHeader: {
    backgroundColor: '#1976d2',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardHeaderTitle: {
    color: '#fff',
    fontWeight: 600,
    fontSize: '16px',
  },
  chevronIcon: {
    color: '#fff',
    fontSize: '1.4rem',
  },
  chatArea: {
    backgroundColor: '#f5f7fa',
    minHeight: '200px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  messageBubble: {
    backgroundColor: '#d4edda',
    borderRadius: '8px 8px 8px 2px',
    padding: '12px 16px',
    maxWidth: '90%',
    alignSelf: 'flex-start',
    boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
  },
  selfBubble: {
    backgroundColor: '#e3f2fd',
    borderRadius: '8px 8px 2px 8px',
    padding: '12px 16px',
    maxWidth: '90%',
    alignSelf: 'flex-end',
    boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
  },
  messageAuthor: {
    fontSize: '15px',
    fontWeight: 700,
    color: '#e65100',
    marginBottom: '4px',
  },
  selfAuthor: {
    fontSize: '15px',
    fontWeight: 700,
    color: '#1976d2',
    marginBottom: '4px',
  },
  messageMeta: {
    fontSize: '13px',
    color: '#777',
    marginBottom: '6px',
  },
  messageText: {
    fontSize: '14px',
    color: '#222',
    lineHeight: 1.5,
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 14px',
    borderTop: '1px solid #d0e4f7',
    backgroundColor: '#fff',
    gap: '8px',
  },
  textInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: '14px',
    color: '#555',
    backgroundColor: 'transparent',
    fontFamily: 'inherit',
    resize: 'none',
  },
  sendIcon: {
    color: '#1976d2',
    fontSize: '1.3rem',
    cursor: 'pointer',
    padding: '4px',
  },
  noMessages: {
    color: '#999',
    fontSize: '14px',
    textAlign: 'center',
    padding: '40px 0',
  },
}));

const CommentsTab = ({ batchId, rowData }) => {
  const classes = useStyles();
  const [inputText, setInputText] = useState('');
  const [chatData, setChatData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(true);
  const chatContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const currentEmail = localStorage.getItem('email') || '';
  const tokenData = jwtDecode(localStorage.getItem("authToken"));
  const isCmo = localStorage.getItem("collabType") === "CMO";

  const fetchComments = () => {
    const data = {
      purchaseOrderNumber: rowData?.poNumber || '',
      schrockenPOLineID: rowData?.schrockenSLID || rowData?.schrockenPOLineID || '',
    };
    axios
      .post(`${getApiBase()}/fetchPOLineItemChatData`, data)
      .then((res) => {
        if (res.data.status) {
          setChatData(res.data.chatData?.poLineItemChatDataResult || []);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (rowData?.poNumber) {
      fetchComments();
    } else {
      setLoading(false);
    }
  }, [rowData]);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  const handleSend = () => {
    if (!inputText.trim()) return;

    setSending(true);
    const userName = tokenData.full_name || '';
    const userEmail = tokenData.email_address || '';
    const data = {
      purchaseOrderNumber: rowData?.poNumber || '',
      vendorMosymphonyID: rowData?.vendorMosymphonyID || '',
      lineNumber: rowData?.lineNumber || rowData?.scheduleLineItemNumber || '',
      schrockenPOLineID: rowData?.schrockenSLID || rowData?.schrockenPOLineID || '',
      chatDataObject: JSON.stringify({
        chatSenderName: userName,
        chatSenderEmail: userEmail,
        chatTimestamp: moment().toISOString(),
        chatData: inputText,
        chatFileHash: '',
        chatFileName: '',
      }),
      createdBy: userName,
      lastUpdatedBy: userName,
    };

    axios
      .post(`${getApiBase()}/addPOLineItemChatData`, data)
      .then((res) => {
        setSending(false);
        if (res.data.status !== false) {
          setInputText('');
          fetchComments();
          setTimeout(scrollToBottom, 200);
        }
      })
      .catch(() => {
        setSending(false);
      });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !sending) {
      handleSend();
    }
  };

  const isSelf = (msg) => {
    return msg.chatSenderEmail === currentEmail || msg.addedByEmail === currentEmail || msg.addedBy === currentEmail;
  };

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.card}>
        {/* Header */}
        <Box className={classes.cardHeader} style={{ cursor: 'pointer' }} onClick={() => setOpen(!open)}>
          <Typography className={classes.cardHeaderTitle}>
            {isCmo? `Comments section for SO ${rowData?.poNumber}` || '' : `Comments section for PO ${rowData?.poNumber}` || '' }
          </Typography>
          <ExpandMoreIcon className={classes.chevronIcon} style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
        </Box>

        {/* Chat area */}
        {open && (<>
        <Box className={classes.chatArea} ref={chatContainerRef}>
          {loading ? (
            <Box style={{ textAlign: 'center', padding: '40px 0' }}>
              <CircularProgress size={24} />
            </Box>
          ) : chatData.length === 0 ? (
            <Typography className={classes.noMessages}>No comments yet</Typography>
          ) : (
            chatData.map((msg, idx) => {
              const self = isSelf(msg);
              return (
                <Box key={idx} className={self ? classes.selfBubble : classes.messageBubble}>
                  <Typography className={self ? classes.selfAuthor : classes.messageAuthor}>
                    {msg.chatSenderName || msg.addedByName || msg.addedBy || 'User'}
                  </Typography>
                  <Typography className={classes.messageMeta}>
                    {(msg.chatTimestamp || msg.addedDate) ? moment(msg.chatTimestamp || msg.addedDate).format('DD-MMM-YYYY HH:mm:ss').toUpperCase() : ''}
                  </Typography>
                  <Typography className={classes.messageText}>
                    {msg.chatData || msg.reviewComments || msg.comment || ''}
                  </Typography>
                </Box>
              );
            })
          )}
        </Box>

        {/* Input row */}
        <Box className={classes.inputRow}>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                setSelectedFile(e.target.files[0]);
              }
            }}
          />
          <AttachFileIcon
            style={{ color: '#888', fontSize: '1.2rem', cursor: 'pointer', transform: 'rotate(45deg)' }}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          />
          {selectedFile && (
            <Typography style={{ fontSize: '0.75rem', color: '#1976d2', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selectedFile.name}
            </Typography>
          )}
          <input
            className={classes.textInput}
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="This is the comments text"
            disabled={sending}
          />
          {sending ? (
            <CircularProgress size={18} />
          ) : (
            <SendIcon className={classes.sendIcon} onClick={handleSend} />
          )}
        </Box>
        </>)}
      </Box>
    </Box>
  );
};

export default CommentsTab;
